/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import cx from 'classnames';
import Title from '@/components/AZCustomComponent/Title';
import { Grid } from '@/components/Grid';
import { useLabel, useLabels } from '@/hooks/useLabels';
import { SmartLink as Link } from '@/utils/smartLink';
import Head from 'next/head';
import React, { useRef, useState, useCallback } from 'react';
import styles from './styles.module.scss';
import type { ShopByCategoryItem, ShopByCategoryItemOldApi } from '../../interface';
import DeviceWrapper from '@/components/AZCustomComponent/DeviceWrapper';
import azCommonStyles from '@/theme/globals.module.scss';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { type RootCategoryV2 } from '@/features/topNav';
import { useAppData } from '@/hooks/useAppData';

type Props = {
  data: {
    name: string;
    shopByCategory:
      | Array<RootCategoryV2 | ShopByCategoryItem | ShopByCategoryItemOldApi>
      | undefined;
  };
};

const labelMap = {
  label_Show_All: 'label_Show_All',
  label_Show_Less: 'label_Show_Less',
};

export const CategoryPartList = ({ data }: Props) => {
  const productsNotAvailable = useLabel('Label_TheseProductsAreCurrentlyNotAvailable');
  const { name, shopByCategory } = data;
  const ref = useRef<HTMLDivElement>(null);
  const labels = useLabels(labelMap);
  const { appData } = useAppData();
  const { deviceType } = appData;
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));

  const [readMore, setReadMore] = useState(deviceType === 'bot');

  const toggleReadMore = useCallback(() => setReadMore((prev) => !prev), []);

  const maxOptions = isDesktop ? 10 : 5;

  const shouldDisplayReadMore =
    !!shopByCategory && shopByCategory.length > maxOptions && !readMore && deviceType !== 'bot';

  const filteredList = readMore ? shopByCategory : shopByCategory?.slice(0, maxOptions);

  return (
    <>
      <DeviceWrapper desktop={true} tablet={true} mobile={true}>
        <div className={styles.topMargin}>
          <div className={shopByCategory?.length === 0 ? styles.categoryPartListTitle : ''}>
            <Title firstText={name} underline variant="md" />
          </div>

          {filteredList?.length ? (
            <Grid
              isULTag
              container
              className={cx(styles.shopByRangeContainer)}
              spacing={1}
              ref={ref}
              data-testid="shop-by-category-list"
            >
              {filteredList.map((item) => {
                const id = 'id' in item ? item.id : item.categoryIdPath;
                const name = 'id' in item ? item.name : item.displayName;

                return (
                  <Grid isListItem item sm={12} lg={6} key={id}>
                    <Link to={item.seoUrl ?? ''}>
                      <div className={`${styles.shopByRangeElement}`}>
                        <span className={styles.categoryItem}>{name}</span>
                      </div>
                    </Link>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <>
              <div className={styles.noResults}>
                <Head>
                  <meta key="metaRobots" name="robots" content="noindex, nofollow" />
                </Head>
                {productsNotAvailable}
              </div>
              <hr />
            </>
          )}
        </div>
      </DeviceWrapper>
      <span className={styles.readMoreSmallProdDesc}>
        {shouldDisplayReadMore && (
          <button
            className={cx(azCommonStyles['az-button-text'], styles.showMoreButton)}
            data-testid="description-read-more"
            onClick={toggleReadMore}
          >
            {labels.label_Show_All}
          </button>
        )}

        {readMore && deviceType !== 'bot' && (
          <button
            className={cx(azCommonStyles['az-button-text'], styles.showMoreButton)}
            data-testid="description-read-less"
            onClick={toggleReadMore}
          >
            {labels.label_Show_Less}
          </button>
        )}
      </span>
    </>
  );
};
